import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import BsDropdownMultiSelect from "@stadium/ui/src/components/dropdown/bs-dropdown-multiselect";
import RangeInput from "@stadium/shared/src/components/filter/range-input";
import { FilterType } from "@stadium/shared/src/components/filter/types";

import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";

import {
  checkIsApplying,
  getFilterCountOffset,
  findOptionByValue,
} from "@stadium/shared/src/components/filter/helpers";
import { selectAppliedFiltersCount } from "@stadium/redux-utils/selectors/filterSelector";

import {
  ANALYTICS_EVENT_ACTION_APPLIED,
  ANALYTICS_EVENT_ACTION_REMOVED,
} from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_FILTER } from "@constants/analytics/filter";
import { PRICE_FILTER_KEY } from "@constants/products/listing";

import style from "../filterDropdown.module.scss";

/**
 * Converts config + data into the data shape expected by BsDropdownMultiSelect
 * (same desktop view as RestrictionFilterDropdown). Skips range-type filters.
 * Dispatches to Redux immediately on change so selected state shows before URL sync.
 */
const flatMapOptions = (options = []) =>
  options.flatMap((opt) => {
    if (opt?.children?.length) return flatMapOptions(opt.children);
    const value = opt?.value ?? opt?.id;
    if (value == null) return [];
    const label = opt.label ?? opt.name ?? String(value);
    return [{ id: String(value), name: label, label }];
  });

const filterConfigToBsMultiSelectData = (config, data, handleFilterChange) => {
  if (!config?.length) return [];

  return config.reduce((acc, item) => {
    if (item.type === FilterType.RANGE || item.id === PRICE_FILTER_KEY) return acc;

    const key = item.id;
    const rawOptions = Array.isArray(item.options)
      ? item.options
      : Object.values(item.options || {});
    const items = flatMapOptions(rawOptions);
    if (!items.length) return acc;

    const raw = data[key];
    const selectedIds = Array.isArray(raw) ? raw.map(String) : [];

    const onChange = ({ key: k, id: valueId }) => {
      const isApplying = !selectedIds.includes(valueId);

      handleFilterChange(
        item.type === FilterType.SINGLE_SELECT ? FilterType.SINGLE_SELECT : FilterType.MULTI_SELECT,
        {
          id: k,
          value: valueId,
          checked: isApplying,
        },
      );
    };

    acc.push({
      key,
      label: item.label,
      items,
      selectedIds,
      type: item.type === FilterType.SINGLE_SELECT ? "radioButton" : "checkbox",
      onChange,
    });
    return acc;
  }, []);
};

/**
 * Desktop-only: builds multiselect data from config + data and renders
 * BsDropdownMultiSelect (with price range as child). Responsive flow uses FilterWithOndemandModal
 */
const FilterV2DesktopMultiselect = ({ config }) => {
  const { onChange, data: filterState } = useFilter();
  const withAnalytics = useWithAnalytics();
  const shopAnalyticsContext = useShopAnalyticsContext();
  const appliedFiltersCount = useSelector(selectAppliedFiltersCount);

  const handleFilterChange = useCallback(
    (filterType, args) => {
      onChange(filterType, args);

      const filterId = args?.id;
      const filterValue = args?.value;
      const currentValues = filterState?.[filterId];

      const isApplying = checkIsApplying(filterType, filterValue, currentValues, args?.checked);
      const countOffset = getFilterCountOffset(filterType, isApplying, currentValues);

      const activeGroup = config?.find((group) => (group.id || group.key) === filterId);
      const activeOption = findOptionByValue(
        Array.isArray(activeGroup?.options) ? activeGroup.options : [],
        filterValue,
      );

      withAnalytics(undefined, {
        track: true,
        target: ANALYTICS_TARGET_FILTER,
        action: isApplying ? ANALYTICS_EVENT_ACTION_APPLIED : ANALYTICS_EVENT_ACTION_REMOVED,
        eventProperties: {
          ...shopAnalyticsContext,
          active_filters_count: appliedFiltersCount + countOffset,
          filter_type: filterId,
          filter_type_name: activeGroup?.label,
          filter_id: filterId === PRICE_FILTER_KEY ? undefined : filterValue,
          filter_name: activeOption?.label ?? activeOption?.name,
          filter_value: isApplying,
        },
      })();
    },
    [onChange, filterState, withAnalytics, appliedFiltersCount, config, shopAnalyticsContext],
  );

  const bsMultiSelectData = useMemo(
    () => filterConfigToBsMultiSelectData(config, filterState, handleFilterChange),
    [config, filterState, handleFilterChange],
  );

  const priceFilterConfig = useMemo(
    () => config?.find((item) => item.id === PRICE_FILTER_KEY),
    [config],
  );

  return (
    <div
      className={`form-dropdown dropdownFilter ${style.catalogFilter} ${style.filterDropdownMenuWrapper}`}
    >
      <BsDropdownMultiSelect
        id="product-filter"
        label="Filters"
        parentLabel="Filters"
        data={bsMultiSelectData}
        className={style.bsDropdown}
        dropDownClass={style.filterDropdownMenu}
        menuWrapper={style.filterDropdownMenuWrapper}
        keyHeadingClass={style.keyHeading}
        filterListWrapperClass={style.filterListWrapper}
        filterListClass={`${style.filterListWrap} ${style.filterListWrapV2}`}
        filterItemClass={`${style.filterDropdownItem} ${style.filterDropdownItemV2}`}
      >
        {/* TODO: Add generic filter */}
        {priceFilterConfig && (
          <li className={`${style.priceList} priceList`}>
            <div className={`${style.keyHeading} keyHeading`}>{priceFilterConfig.label}</div>
            <RangeInput
              item={priceFilterConfig}
              value={filterState?.[PRICE_FILTER_KEY]}
              onChange={(val) =>
                handleFilterChange(FilterType.RANGE, { id: PRICE_FILTER_KEY, value: val })
              }
              showRangeInputs={false}
            />
          </li>
        )}
      </BsDropdownMultiSelect>
    </div>
  );
};

export default FilterV2DesktopMultiselect;
